import React, { useState, useEffect, useCallback } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { styled } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { PLAN_TYPE_MAP } from "../../utils/constants";
import { isTrustedParentMessage } from "../../utils/iframeMessaging";

import employerSvc from "../../services/employer/employerSvc";
import EnrollDataContainer from "./components/EnrollDataContainer"; // <-- IMPORTANT: Use Container now

// Styled Autocomplete for consistency
const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    paddingLeft: theme.spacing(1),
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "#e0e0e0",
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: "#297ac2",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#297ac2",
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 500,
    color: "#666",
  },
}));

function SearchEnroll() {
  const routerLocation = useLocation();
  const employerId = routerLocation.pathname.split("/")[2];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // New: State to store parent website
  const [parentWebsite, setParentWebsite] = useState(null);

  // Debug flag - set to true to enable console logs
  const DEBUG_ROUTING = false;

  const debugLog = useCallback((message, data = null) => {
    if (DEBUG_ROUTING) {
      if (data) {
        console.log('SearchEnroll:', message, data);
      } else {
        console.log('SearchEnroll:', message);
      }
    }
  }, [DEBUG_ROUTING]);

  // Listen for postMessage from parent for website info
  useEffect(() => {
    function handleMessage(event) {
      if (!isTrustedParentMessage(event)) {
        debugLog("Ignoring message from untrusted source:", event.origin);
        return;
      }
      if (event.data && event.data.website) {
        setParentWebsite(event.data.website);
        debugLog("Received parent website via postMessage:", event.data.website);
      } else if (event.data && event.data.type === 'WEBSITE_INFO' && event.data.website) {
        setParentWebsite(event.data.website);
        debugLog("Received parent website via WEBSITE_INFO:", event.data.website);
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [debugLog]);

  // Compute websiteName, prefer parentWebsite if present
  const websiteName = React.useMemo(() => {
    if (parentWebsite) {
      switch (parentWebsite) {
        case "region10rams.org":
          return "Region 10 RAMS";
        case "calstrs403bcomply.com":
          return "CALSTRS";
        case "tcgservices.com":
          return "TCG Services";
        // Add more mappings as needed
        default:
          return parentWebsite;
      }
    }
    // Default to TCG Services if no parentWebsite provided
    return "TCG Services";
  }, [parentWebsite]);

  const [loading, setLoading] = useState(true);
  const [employers, setEmployers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [approvedVendors, setApprovedVendors] = useState([]);
  const [employerInfo, setEmployerInfo] = useState({
    employerName: null,
    employerState: null,
  });

  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [showAvailablePlans, setShowAvailablePlans] = useState(false);
  const [noPlansFound, setNoPlansFound] = useState(false);

  useEffect(() => {
    // Make API call if we have a parentWebsite OR if we're in standalone mode (localhost)
    // This ensures we make the call for all parent websites, including tcgservices.com
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (parentWebsite || isLocalhost) {
      const getEmployers = async () => {
        try {
          setLoading(true);
          debugLog("Making API call with websiteName:", websiteName);
          debugLog("API call parameters:", {
            website: websiteName,
            page: "enrollSearch",
          });
          
          const response = await employerSvc.getEmployersByWebsite({
            website: websiteName,
            page: "enrollSearch",
          });
          
          debugLog("API response:", response);
          setEmployers(response);
        } catch (error) {
          console.error("API call error:", error);
        } finally {
          setLoading(false);
        }
      };
      getEmployers();
    }
  }, [websiteName, parentWebsite, debugLog]);

  useEffect(() => {
    if (employerId) {
      const getEmployerDetails = async () => {
        try {
          setLoading(true);
          const { employerInfo, plans, approvedVendors } =
            await employerSvc.getEmployerDetails({
              employerIdForURL: employerId,
              page: "enrollSearch",
            });

          setPlans(plans);
          setApprovedVendors(approvedVendors);
          setEmployerInfo(employerInfo);

          if (plans.length > 0) {
            setShowAvailablePlans(true);
            setNoPlansFound(false);
          } else {
            setShowAvailablePlans(false);
            setNoPlansFound(true);
          }
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      };
      getEmployerDetails();
    }
  }, [employerId]);

  useEffect(() => {
    // Convert each plan into a { label } object using PLAN_TYPE_MAP
    const planTypeObjects = plans.map((item) => {
      const key = item.planUniqueId.split("_")[1];
      return { label: PLAN_TYPE_MAP[key]?.label || key };
    });
    setPlanTypes(planTypeObjects);

    if (employerId && employers.length > 0) {
      const matched = employers.find((e) => e.employerIdForURL === employerId);
      setSelectedEmployer(matched);
    }
  }, [plans, employerId, employers]);

  return (
    <Container
      component="main"
      maxWidth="lg"
      sx={{ 
        mt: { xs: 2, md: 6 }, 
        mb: { xs: 4, md: 6 },
        px: { xs: 2, md: 3 }
      }}
    >
      {loading ? (
        <Grid container justifyContent="center">
          <CircularProgress size={40} />
        </Grid>
      ) : (
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="stretch">
          {/* LEFT COLUMN: Search + Plan List */}
          <Grid
            item
            xs={12}
            md={8}
            order={{ xs: 1, md: 1 }}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Search Box Card */}
            <Card sx={{ 
              p: isMobile ? 3 : 4, 
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)", 
              borderRadius: "12px",
              border: "1px solid #e0e0e0"
            }}>
              <CardContent>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h4"
                    sx={{ 
                      color: "#297ac2", 
                      fontWeight: "bold", 
                      mb: 3,
                      fontSize: { xs: "1.5rem", md: "2rem" }
                    }}
                  >
                    Let’s Begin Your Journey to Financial Independence!
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{ 
                      fontWeight: "bold", 
                      mb: isMobile ? 3 : 4,
                      fontSize: { xs: "1rem", md: "1.25rem" }
                    }}
                  >
                    Begin by entering the name of your employer:
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ 
                      color: "gray", 
                      mb: isMobile ? 3 : 4,
                      fontSize: { xs: "0.875rem", md: "1rem" }
                    }}
                  >
                    (Type at least 3 characters to start searching)
                  </Typography>

                  <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
                    <Grid item xs={12} sm={9}>
                      <StyledAutocomplete
                        id="clientSearchBar"
                        value={selectedEmployer}
                        inputValue={searchValue}
                        open={
                          searchValue.length >= 3 && selectedEmployer === null
                        }
                        forcePopupIcon={false}
                        noOptionsText="No employers found."
                        getOptionLabel={(option) => option.employerName || ""}
                        onChange={(_, employer) => setSelectedEmployer(employer)}
                        onInputChange={(_, value) => setSearchValue(value)}
                        options={employers}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Employer Name"
                            fullWidth
                            size={isMobile ? "small" : "medium"}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Button
                        fullWidth
                        variant="contained"
                        component={RouterLink}
                        to={
                          selectedEmployer
                            ? `/search-enroll/${selectedEmployer.employerIdForURL}`
                            : ""
                        }
                        disabled={!selectedEmployer}
                        sx={{
                          fontWeight: 600,
                          backgroundColor: "#041E42",
                          "&:hover": { 
                            backgroundColor: "#062D4C",
                            transform: "translateY(-1px)",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.2)"
                          },
                          fontSize: isMobile ? "0.9rem" : "1.1rem",
                          padding: isMobile ? "12px 16px" : "14px 24px",
                          borderRadius: "8px",
                          transition: "all 0.2s ease-in-out",
                          textTransform: "none"
                        }}
                      >
                        Search
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>

            {/* Plan Results / "No Plans Found" */}
            {!loading && showAvailablePlans && employerInfo.employerName ? (
              <Box sx={{ mt: { xs: 4, md: 5 } }}>
                {/*
                  Instead of <EnrollData> directly,
                  we use our EnrollDataContainer
                */}
                <EnrollDataContainer
                  planTypes={planTypes}
                  plans={plans}
                  employerName={employerInfo.employerName}
                  approvedVendors={approvedVendors}
                  empState={employerInfo.state}
                  employerId={employerId}
                  reliusWeb={selectedEmployer?.relius_web}
                />
              </Box>
            ) : !loading && noPlansFound ? (
              <Typography
                variant="h6"
                color="error"
                align="center"
                sx={{ mt: 4 }}
              >
                No Plans Found. If you believe this is a mistake, please contact
                1-800-943-9179.
              </Typography>
            ) : null}
          </Grid>

          {/* RIGHT COLUMN: Enrollment Hotline */}
          <Grid
            item
            xs={12}
            md={4}
            order={{ xs: 3, md: 2 }}
            sx={{
              display: "flex",
            }}
          >
            <Card
              sx={{
                backgroundColor: "#2D7A99",
                color: "white",
                textAlign: "center",
                p: { xs: 3, md: 4 },
                borderRadius: "12px",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                border: "1px solid #1e5a6b",
                minHeight: { xs: "auto", md: "400px" }
              }}
            >
              <Box sx={{ mb: 2, width: "100%", textAlign: "center" }}>
                <img
                  src="https://tcgservices.com/wp-content/uploads/2016/01/tcg services enrollment contact"
                  alt="Enrollment Contact"
                  style={{ maxWidth: "80%", height: "auto" }}
                />
              </Box>

              <Typography variant="h6" sx={{ fontWeight: "bold", mt: 1 }}>
                Enrollment Hotline
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                Call{" "}
                <Link
                  href="tel:800-943-9179"
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  800-943-9179
                </Link>{" "}
                for help getting started.
              </Typography>

              <Box sx={{ mt: 1 }}>
                <img
                  src="https://tcgservices.com/wp-content/uploads/2016/01/tcg%20services%20enrollment%20calendar"
                  alt="Enrollment Calendar"
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </Box>

              <Typography variant="body2" sx={{ mt: 1 }}>
                <Link
                  href="https://tcgservices.com/appointment/"
                  target="_blank"
                  sx={{ color: "white", textDecoration: "underline" }}
                >
                  Click here
                </Link>{" "}
                to set up an appointment.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}

export default SearchEnroll;
